<template>
  <div class="form-wizard-step">
    <div class="form-wizard-content text-center">
      <div class="container">
        <TilesSelect
          :surfaces="surfaces"
          :selectedShape="formState.tileShape.id"
          :current="tile"
          :description="description"
          @on-select="updateState"
          @open-gallery-fullscreen="$emit('open-gallery-fullscreen')"
        />
      </div>
    </div>

    <div class="form-wizard-footer">
      <div class="form-wizard-actions container">
        <div class="row">
          <div class="col-md-6 order-1 order-md-0">
            <button
              data-test="previous-step"
              @click="stepBack"
              :disabled="isLoading"
              class="btn btn-secondary btn-block"
            >
              {{ t('steps.tiles.buttons.prev') }}
            </button>
          </div>
          <div class="col-md-6">
            <button
              data-test="next-step"
              @click="validateStep"
              :disabled="isLastStep || isLoading"
              class="btn btn-primary btn-block"
            >
              {{ !isLoading ? t('steps.tiles.buttons.next') : t('general.loading') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, onBeforeMount, onUnmounted, ref, reactive, toRefs } from 'vue'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

import httpClient from '@/services/api/httpClient'
import { isCalculateStep } from '@/services/language'
import useFormWizard from '@/hooks/formWizard'
import useFormState from '@/hooks/formState'
import useGtmEvent from '@/hooks/gtm'
import useGallery from '@/hooks/gallery'

import TilesSelect from '@/components/Calculator/TilesSelect'

export default defineComponent({
  name: 'TilesStep',
  emits: ['open-gallery-fullscreen'],
  components: {
    TilesSelect
  },
  setup() {
    const { nextStep, prevStep, isLastStep, currentStepNumber } = useFormWizard()
    const { updateFormState, formState, getFieldValue } = useFormState()
    const { trackStep } = useGtmEvent()
    const { setGallery } = useGallery()
    const toast = useToast()
    const { t } = useI18n()

    const surfaces = ref([])
    const description = ref('')
    const isLoading = ref(false)

    const state = reactive({
      tile: getFieldValue('tile') || false
    })

    onBeforeMount(() => {
      fetchStepData()
      document.addEventListener('keypress', submitHandler)
    })

    onUnmounted(() => {
      document.removeEventListener('keypress', submitHandler)
    })

    const submitHandler = e => e.key === 'Enter' && validateStep()

    const fetchStepData = async () => {
      try {
        const { data } = await httpClient.get('surfaces', '', {
          tile_shape: formState.tileShape.id
        })

        surfaces.value = data.data.surfaces || []
        description.value = data.data.description || ''
        if (!getFieldValue('tile') && surfaces.value.length) {
          updateState(surfaces.value[0].tiles[0])
        }

        return Promise.resolve()
      } catch (error) {
        toast.error(t('errors.data'))
      }
    }

    const updateState = tile => {
      state.tile = tile
    }

    const stepBack = () => {
      prevStep()
    }

    const validateStep = async () => {
      if (isLoading.value) return

      try {
        isLoading.value = true

        await httpClient.post('tiles', { tile: state.tile.id })
        updateFormState({ ...state })

        if (!isCalculateStep('Tiles')) {
          nextStep({ label: `${state.tile.title} ${state.tile.color}` })
          return
        }

        const calculatePayload = {
          degree: formState.degree,
          roof_type: formState.roofType.id,
          sides: formState.sides,
          tile_shape: formState.tileShape.id,
          tile: state.tile.id,
          selected_package: formState.selectedPackage?.id
        }

        const { data } = await httpClient.post('calculate', calculatePayload)

        trackStep({
          category: `step${currentStepNumber.value + 1}`,
          action: 'veglegesites',
          label: `${data.data.tile.title} ${data.data.tile.color}`
        })

        state.results = data.data

        updateFormState({ ...state })

        setGallery(data.data.gallery)

        nextStep({ label: `${state.tile.title} ${state.tile.color}` })
      } catch (error) {
        toast.error(t('errors.save'))
        console.log(error)
      } finally {
        isLoading.value = false
      }
    }

    return {
      nextStep,
      stepBack,
      isLastStep,
      isLoading,
      surfaces,
      description,
      formState,
      updateState,
      validateStep,
      ...toRefs(state),
      t
    }
  }
})
</script>
